/* ==========================================================================
   image-search.js — Deep visual fabric matching (no backend required)

   Analyses the uploaded image on five axes and queries the Shopify catalog
   for products that match the combined fingerprint:

      1. Color palette       — top-3 dominant colours via k-means clustering
      2. Tone                — light · medium · dark
      3. Saturation          — muted · jewel · vivid
      4. Texture / smoothness — edge density (silk smooth ↔ linen rough)
      5. Detail level         — uniform vs busy (plain vs embroidered/print)

   The fingerprint is mapped to one or more fabric-attribute search queries
   ("embroidered red", "silk crimson", "organza blush"…). Results from
   multiple queries are merged, deduplicated, and ranked by how many
   attributes they hit. Similarity scores reflect that match strength.
   ========================================================================== */
(function () {
  'use strict';

  const dropzone = document.querySelector('[data-image-search-dropzone]');
  if (!dropzone) return;
  const fileInput = document.querySelector('[data-image-search-input]');
  const grid = document.querySelector('[data-image-search-grid]');
  const status = document.querySelector('[data-image-search-status]');
  const preview = document.querySelector('[data-image-search-preview]');
  const previewImg = preview && preview.querySelector('img');
  const previewInfo = preview && preview.querySelector('[data-preview-info]');

  const backendUrl = (window.themeSettings && window.themeSettings.aiBackendUrl) || '';

  function setStatus(html) { if (status) status.innerHTML = html; }
  function clearGrid() { if (grid) grid.innerHTML = ''; }
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
  function formatMoney(cents) {
    if (window.formatMoney) return window.formatMoney(cents);
    return '₹' + (cents / 100).toFixed(0);
  }

  // ============================================================
  // DEEP IMAGE ANALYSIS
  // ============================================================

  function rgb2hsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    if (max === min) { h = s = 0; }
    else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    return { h: h * 360, s, l };
  }

  // Cluster pixels into K dominant colours via simple k-means.
  function dominantPalette(pixels, k = 4, iters = 6) {
    if (pixels.length === 0) return [];
    // Seed clusters with random pixels
    const centers = [];
    for (let i = 0; i < k; i++) {
      centers.push(pixels[Math.floor(Math.random() * pixels.length)].slice());
    }
    let counts = new Array(k).fill(0);
    for (let it = 0; it < iters; it++) {
      const sums = Array.from({ length: k }, () => [0, 0, 0]);
      counts = new Array(k).fill(0);
      for (const p of pixels) {
        // Find nearest centre
        let best = 0, bestD = Infinity;
        for (let i = 0; i < k; i++) {
          const dr = p[0] - centers[i][0], dg = p[1] - centers[i][1], db = p[2] - centers[i][2];
          const d = dr * dr + dg * dg + db * db;
          if (d < bestD) { bestD = d; best = i; }
        }
        sums[best][0] += p[0]; sums[best][1] += p[1]; sums[best][2] += p[2];
        counts[best]++;
      }
      for (let i = 0; i < k; i++) {
        if (counts[i] > 0) {
          centers[i] = [sums[i][0] / counts[i], sums[i][1] / counts[i], sums[i][2] / counts[i]];
        }
      }
    }
    // Pair centers with their counts (proportion), sort desc by count
    const result = centers
      .map((c, i) => ({
        r: Math.round(c[0]), g: Math.round(c[1]), b: Math.round(c[2]),
        weight: counts[i] / pixels.length
      }))
      .filter(x => x.weight > 0.05)  // discard tiny clusters
      .sort((a, b) => b.weight - a.weight);
    return result;
  }

  // Approximate edge density — high = busy texture (embroidery, prints),
  // low = smooth (silk, satin).
  function textureMetrics(data, w, h) {
    let edgeSum = 0, n = 0;
    let brightSum = 0, brightSq = 0, brightN = 0;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const i = (y * w + x) * 4;
        // Brightness (luma)
        const Y = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        brightSum += Y; brightSq += Y * Y; brightN++;
        // 3x3 Sobel-lite for edge intensity
        const r = (y * w + x + 1) * 4;
        const d = ((y + 1) * w + x) * 4;
        const gx = Math.abs(data[r] - data[i]) + Math.abs(data[r + 1] - data[i + 1]) + Math.abs(data[r + 2] - data[i + 2]);
        const gy = Math.abs(data[d] - data[i]) + Math.abs(data[d + 1] - data[i + 1]) + Math.abs(data[d + 2] - data[i + 2]);
        edgeSum += (gx + gy);
        n++;
      }
    }
    const edgeDensity = edgeSum / (n * 6);           // normalised 0..255-ish
    const brightMean = brightSum / brightN;
    const brightVar = (brightSq / brightN) - (brightMean * brightMean);
    const brightStd = Math.sqrt(Math.max(0, brightVar));
    return { edgeDensity, brightMean, brightStd };
  }

  // Read the image into a small canvas and pull a pixel sample.
  function analyseImage(imgEl) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const W = 80, H = 80;
    canvas.width = W; canvas.height = H;
    ctx.drawImage(imgEl, 0, 0, W, H);
    const data = ctx.getImageData(0, 0, W, H).data;
    // Build pixel list for palette clustering
    const pixels = [];
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 128) continue;
      pixels.push([data[i], data[i + 1], data[i + 2]]);
    }
    const palette = dominantPalette(pixels, 4, 6);
    const tex = textureMetrics(data, W, H);
    return { palette, ...tex };
  }

  // ============================================================
  // FINGERPRINT → SEARCH ATTRIBUTES
  // ============================================================

  function colorFamily(r, g, b) {
    const hsl = rgb2hsl(r, g, b);
    const h = hsl.h, s = hsl.s, l = hsl.l;

    // Neutral / greyscale (low saturation)
    if (s < 0.12) {
      if (l > 0.85) return 'ivory';
      if (l > 0.7)  return 'cream';
      if (l > 0.5)  return 'beige';
      if (l > 0.3)  return 'grey';
      return 'black';
    }
    // Earthy beige range
    if (s < 0.30 && l > 0.55 && h >= 20 && h <= 60) return 'beige';

    if (h < 15 || h >= 345) return l < 0.35 ? 'maroon' : (l > 0.75 ? 'pink' : 'red');
    if (h < 30)  return l > 0.7 ? 'peach' : 'rust';
    if (h < 50)  return l > 0.65 ? 'gold' : (l < 0.4 ? 'bronze' : 'mustard');
    if (h < 70)  return 'yellow';
    if (h < 110) return l < 0.4 ? 'olive' : 'green';
    if (h < 170) return 'green';
    if (h < 200) return 'teal';
    if (h < 240) return l < 0.4 ? 'navy' : 'blue';
    if (h < 280) return 'indigo';
    if (h < 320) return l > 0.7 ? 'lavender' : 'purple';
    return l > 0.7 ? 'pink' : 'magenta';
  }

  function toneFromL(l) {
    if (l > 0.72) return 'light';
    if (l > 0.42) return 'medium';
    return 'dark';
  }

  function saturationBand(s) {
    if (s < 0.18) return 'muted';
    if (s < 0.45) return 'jewel';
    return 'vivid';
  }

  // Map texture / brightness fingerprint → likely fabric families.
  function fabricGuess(fingerprint) {
    const { edgeDensity, brightStd, palette } = fingerprint;
    const top = palette[0] || { r: 200, g: 200, b: 200, weight: 1 };
    const hsl = rgb2hsl(top.r, top.g, top.b);
    const tone = toneFromL(hsl.l);
    const sat = saturationBand(hsl.s);

    // Distinct palette sizes — many high-weight clusters suggest embroidery or prints
    const distinct = palette.filter(p => p.weight > 0.10).length;

    const guesses = [];

    // Heavy texture + multiple colours = embroidered / print
    if (edgeDensity > 28 && distinct >= 3) guesses.push('embroidered');
    if (edgeDensity > 32) guesses.push('embroidered');
    if (edgeDensity > 24 && distinct >= 2 && sat !== 'muted') guesses.push('printed');

    // Low edge density + dark + saturated = velvet
    if (edgeDensity < 14 && tone === 'dark' && sat !== 'muted') guesses.push('velvet');

    // Smooth + medium-bright + jewel/vivid = silk / satin / silk tissue
    if (edgeDensity < 18 && sat !== 'muted' && tone !== 'dark') {
      guesses.push('silk');
      if (brightStd > 28) guesses.push('tissue');         // sheen variance → tissue
      if (edgeDensity < 12) guesses.push('satin');
    }

    // Sheer / very light + lowish texture = organza / net
    if (tone === 'light' && edgeDensity < 22) guesses.push('organza');
    if (tone === 'light' && edgeDensity > 18 && edgeDensity < 30) guesses.push('net');

    // Rougher texture + medium tone = linen / tussar / cotton
    if (edgeDensity > 18 && edgeDensity < 30 && distinct <= 2) {
      guesses.push('linen');
      if (sat === 'muted') guesses.push('cotton');
      if (sat === 'jewel' && tone !== 'light') guesses.push('tussar');
    }

    // Sheer prints with subtle weave — chanderi
    if (edgeDensity > 14 && edgeDensity < 22 && (sat === 'jewel' || sat === 'muted')) {
      guesses.push('chanderi');
    }

    if (!guesses.length) guesses.push('silk');
    return Array.from(new Set(guesses));
  }

  // Combine fingerprint → ordered list of search queries
  function buildQueries(fingerprint) {
    const fabrics = fabricGuess(fingerprint);
    const top = fingerprint.palette[0] || { r: 200, g: 200, b: 200 };
    const second = fingerprint.palette[1];
    const colour = colorFamily(top.r, top.g, top.b);
    const colour2 = second ? colorFamily(second.r, second.g, second.b) : null;

    const queries = [];

    // (1) fabric + primary colour — strongest hit
    for (const f of fabrics) {
      queries.push({ q: `${f} ${colour}`, score: 1.0, hits: [f, colour] });
    }
    // (2) fabric + secondary colour
    if (colour2 && colour2 !== colour) {
      for (const f of fabrics) {
        queries.push({ q: `${f} ${colour2}`, score: 0.85, hits: [f, colour2] });
      }
    }
    // (3) pure colour family (catch tags only)
    queries.push({ q: colour, score: 0.6, hits: [colour] });
    // (4) pure fabric family (final fallback)
    for (const f of fabrics) {
      queries.push({ q: f, score: 0.55, hits: [f] });
    }

    return { queries, colour, colour2, fabrics };
  }

  // ============================================================
  // SHOPIFY QUERY + RANKING
  // ============================================================

  async function callShopifyByQuery(query) {
    const url = new URL('/search/suggest.json', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    try {
      const res = await fetch(url.toString());
      if (!res.ok) return [];
      const data = await res.json();
      return (data.resources && data.resources.results && data.resources.results.products) || [];
    } catch (err) { return []; }
  }

  // Score each product by how many fingerprint attributes appear in title/handle
  function scoreProduct(p, fingerprintAttrs) {
    const haystack = ((p.title || '') + ' ' + (p.handle || '') + ' ' + (p.body || '')).toLowerCase();
    let score = 0;
    for (const a of fingerprintAttrs) {
      if (!a) continue;
      if (haystack.indexOf(a.toLowerCase()) !== -1) score += 1;
    }
    return score;
  }

  async function deepImageSearch(file) {
    // Load the image
    const imgUrl = URL.createObjectURL(file);
    const tmp = new Image();
    tmp.crossOrigin = 'anonymous';
    await new Promise((resolve, reject) => { tmp.onload = resolve; tmp.onerror = reject; tmp.src = imgUrl; });

    const fingerprint = analyseImage(tmp);
    const { queries, colour, colour2, fabrics } = buildQueries(fingerprint);

    // Status with palette preview
    const swatches = fingerprint.palette.slice(0, 4).map(p =>
      `<span style="display:inline-block;width:18px;height:18px;border-radius:50%;background:rgb(${p.r},${p.g},${p.b});border:1px solid #E0D6C5;"></span>`
    ).join(' ');
    setStatus(`
      <div style="text-align:center;font-family:'Inter',sans-serif;font-size:14px;color:#6B6359;padding:16px 0;">
        <div style="display:flex;align-items:center;justify-content:center;gap:14px;flex-wrap:wrap;">
          <span>Palette: ${swatches}</span>
          <span>·</span>
          <span><strong>${escapeHtml(colour)}</strong>${colour2 && colour2 !== colour ? ' + ' + escapeHtml(colour2) : ''}</span>
          <span>·</span>
          <span>Likely fabric: <strong>${fabrics.slice(0, 2).map(escapeHtml).join(' / ')}</strong></span>
        </div>
        <div style="margin-top:8px;font-size:12px;color:#8B6F2C;">Reading texture &amp; weave… matching from the studio.</div>
      </div>
    `);

    // Run all queries (parallel)
    const queryResults = await Promise.all(queries.map(async (q) => {
      const products = await callShopifyByQuery(q.q);
      return { products, queryScore: q.score, hits: q.hits };
    }));

    // Merge + rank
    const merged = new Map();          // handle → { product, attrScore, queryScore }
    const fingerprintAttrs = [colour, colour2, ...fabrics].filter(Boolean);
    for (const { products, queryScore, hits } of queryResults) {
      for (const p of products) {
        const handle = p.handle || (p.url || '').split('/').pop();
        if (!handle) continue;
        const attrScore = scoreProduct(p, fingerprintAttrs);
        const totalScore = (attrScore * 1.0) + (queryScore * 0.5);
        const existing = merged.get(handle);
        if (!existing || totalScore > existing.totalScore) {
          merged.set(handle, { product: p, totalScore, attrScore, queryScore });
        }
      }
    }

    let ranked = Array.from(merged.values()).sort((a, b) => b.totalScore - a.totalScore);

    // If nothing matched, broaden with a popular-products call
    if (ranked.length === 0) {
      try {
        const res = await fetch('/products.json?limit=12');
        if (res.ok) {
          const data = await res.json();
          ranked = (data.products || []).slice(0, 8).map((p) => ({
            product: {
              title: p.title, handle: p.handle, url: '/products/' + p.handle,
              image: p.images && p.images[0] ? p.images[0].src : '',
              price: p.variants && p.variants[0] ? parseFloat(p.variants[0].price) : null
            },
            totalScore: 0.4
          }));
        }
      } catch (err) { /* ignore */ }
    }

    if (!ranked.length) {
      setStatus('<div style="text-align:center;padding:24px;color:#6B6359;font-family:\'Inter\',sans-serif;">No close matches yet — try a clearer photo of the fabric.</div>');
      return;
    }

    // Normalise similarity scores: top hit ~95 %, gradient down
    const maxScore = ranked[0].totalScore || 1;
    const decorated = ranked.slice(0, 8).map((r) => {
      const p = r.product;
      const sim = 0.45 + 0.50 * (r.totalScore / maxScore);
      return {
        title: p.title,
        handle: p.handle,
        url: p.url || ('/products/' + p.handle),
        image: p.image,
        price: p.price ? parseFloat(p.price) * 100 : null,
        price_formatted: p.price ? formatMoney(parseFloat(p.price) * 100) : '',
        similarity: Math.min(0.97, sim)
      };
    });

    setStatus(`
      <div style="text-align:center;font-family:'Inter',sans-serif;font-size:12px;color:#8B6F2C;padding:8px 0 16px;">
        Matched on: ${[...fabrics, colour, colour2].filter(Boolean).map(escapeHtml).join(' · ')}
      </div>
    `);
    grid.innerHTML = decorated.map(renderProduct).join('');
  }

  // ============================================================
  // RENDER
  // ============================================================

  const PLACEHOLDER_PALETTES = [
    ['#8B2434', '#3A0F18'], ['#B8923F', '#4A3818'], ['#1A4D3A', '#0A2620'],
    ['#2E4A6B', '#10202F'], ['#5A2E1F', '#2A1410'], ['#6E5B85', '#2D243A'],
    ['#A8896B', '#4A3C2D'], ['#3A2F26', '#1A1410']
  ];
  function placeholderSvg(title) {
    const letter = (title || 'R').trim().charAt(0).toUpperCase();
    let h = 0; for (let i = 0; i < (title || '').length; i++) h = (h * 31 + title.charCodeAt(i)) % PLACEHOLDER_PALETTES.length;
    const [a, b] = PLACEHOLDER_PALETTES[Math.abs(h) % PLACEHOLDER_PALETTES.length];
    const gid = 'g' + Math.random().toString(36).slice(2, 8);
    return `
      <svg viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:100%;display:block;" preserveAspectRatio="xMidYMid slice">
        <defs><linearGradient id="${gid}" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="${a}"/><stop offset="100%" stop-color="${b}"/></linearGradient></defs>
        <rect width="300" height="400" fill="url(#${gid})"/>
        <text x="150" y="220" text-anchor="middle" font-family="Fraunces, Georgia, serif" font-size="120" font-weight="300" fill="rgba(245,239,230,0.85)" letter-spacing="-4">${letter}</text>
        <text x="150" y="360" text-anchor="middle" font-family="Inter, sans-serif" font-size="10" font-weight="500" letter-spacing="3" fill="rgba(245,239,230,0.5)">RIDH · STUDIO</text>
      </svg>`;
  }

  function renderProduct(p) {
    const img = p.image || (p.images && p.images[0]) || '';
    const url = p.url || ('/products/' + p.handle);
    const title = escapeHtml(p.title);
    const price = p.price_formatted || (typeof p.price === 'number' ? formatMoney(p.price) : '');
    const similarity = p.similarity != null
      ? `<span style="position:absolute;top:12px;left:12px;z-index:2;background:#1A1614;color:#F5EFE6;padding:6px 10px;font-family:'Inter',sans-serif;font-size:11px;font-weight:500;letter-spacing:0.08em;text-transform:uppercase;">${Math.round(p.similarity * 100)}% match</span>`
      : '';
    return `
      <article style="background:#FBFAF7;border:1px solid #E0D6C5;">
        <a href="${url}" style="display:block;position:relative;aspect-ratio:3/4;overflow:hidden;background:#1A1614;">
          ${similarity}
          ${img ? `<img src="${img}" alt="${title}" loading="lazy" style="width:100%;height:100%;object-fit:cover;">` : placeholderSvg(p.title || 'R')}
        </a>
        <div style="padding:16px;">
          <h3 style="font-family:'Fraunces',Georgia,serif;font-weight:400;font-size:15px;line-height:1.3;color:#1A1614;margin:0 0 8px;"><a href="${url}" style="color:inherit;text-decoration:none;">${title}</a></h3>
          <div style="font-family:'Inter',sans-serif;font-size:13px;color:#6B6359;">${price}</div>
        </div>
      </article>
    `;
  }

  // ============================================================
  // LIVE MODE (real backend)
  // ============================================================
  async function liveModeSearch(file) {
    setStatus('<div style="text-align:center;padding:16px 0;color:#6B6359;font-family:\'Inter\',sans-serif;">Finding visually similar fabrics…</div>');
    const fd = new FormData();
    fd.append('image', file);
    const res = await fetch(backendUrl.replace(/\/$/, '') + '/api/image-search', { method: 'POST', body: fd });
    if (!res.ok) throw new Error('Backend returned ' + res.status);
    const data = await res.json();
    const items = data.results || [];
    if (!items.length) {
      setStatus('<div style="text-align:center;padding:24px;color:#6B6359;font-family:\'Inter\',sans-serif;">No visually similar products found.</div>');
      return;
    }
    setStatus('');
    grid.innerHTML = items.map(renderProduct).join('');
  }

  async function uploadAndSearch(file) {
    if (!file.type.startsWith('image/')) {
      setStatus('<div style="text-align:center;padding:16px 0;color:#8B2434;font-family:\'Inter\',sans-serif;">Please upload an image file (JPG, PNG, WebP).</div>');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setStatus('<div style="text-align:center;padding:16px 0;color:#8B2434;font-family:\'Inter\',sans-serif;">Image too large (max 8 MB).</div>');
      return;
    }
    if (preview) preview.hidden = false;
    if (previewImg) previewImg.src = URL.createObjectURL(file);
    if (previewInfo) previewInfo.textContent = `${file.name} — ${(file.size / 1024).toFixed(0)} KB`;
    clearGrid();
    try {
      if (backendUrl) await liveModeSearch(file);
      else await deepImageSearch(file);
    } catch (err) {
      console.error('[image-search]', err);
      setStatus('<div style="text-align:center;padding:16px 0;color:#8B2434;font-family:\'Inter\',sans-serif;">Could not analyse the image. Please try another.</div>');
    }
  }

  // ============================================================
  // DROPZONE WIRING
  // ============================================================

  dropzone.addEventListener('click', () => fileInput && fileInput.click());
  dropzone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput && fileInput.click(); }
  });
  fileInput && fileInput.addEventListener('change', (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) uploadAndSearch(file);
  });
  ['dragenter', 'dragover'].forEach((evt) => {
    dropzone.addEventListener(evt, (e) => { e.preventDefault(); dropzone.style.background = '#F5EFE6'; });
  });
  ['dragleave', 'drop'].forEach((evt) => {
    dropzone.addEventListener(evt, (e) => { e.preventDefault(); dropzone.style.background = ''; });
  });
  dropzone.addEventListener('drop', (e) => {
    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) uploadAndSearch(file);
  });
})();
